import { __classPrivateFieldGet, __classPrivateFieldSet, __awaiter } from '../external_node_modules/tslib/tslib.es6.js';
import { addEvent, createAnalytics } from '../analytics/index.js';
import { DWH_EVENTS } from '../constants/dwhEvents.js';
import { EVENTS } from '../constants/events.js';
import { PLATFORMS } from '../constants/platforms.js';
import { PURCHASE_STATUSES } from '../constants/purchaseStatuses.js';
import events from './event/index.js';
import { EpicGamesStorePlatform } from './platform/EpicGamesStore/index.js';
import { SteamPlatform } from './platform/Steam/index.js';
import { WebPlatform } from './platform/Web/index.js';
import { WinStorePlatform } from './platform/WinStore/index.js';
import settings from '../settings.js';
import { purchaseProduct } from '../store/actions/purchase.js';
import activePaymentMethod from '../store/activePaymentMethod.js';
import currentProduct, { changeCurrentProduct } from '../store/currentProduct.js';
import { updateProduct } from '../store/products.js';
import purchaseTwoFactor from '../store/purchaseTwoFactor.js';
import transactionId from '../store/transactionId.js';
import tsv from '../store/tsv.js';
import detectPlatform from '../utils/detectPlatform.js';
import { log } from '../utils/logger.js';
import { openUrl } from '../utils/openUrl.js';
import { getStorageItem, setStorageItem } from '../utils/storage.js';

var _WowsCommerce_instances, _WowsCommerce_platform, _WowsCommerce_platformProvider, _WowsCommerce_checkAccountCompletionIntervalId, _WowsCommerce_checkAccountCompletionIterationCount, _WowsCommerce_currentProduct, _WowsCommerce_activePaymentMethod, _WowsCommerce_tsv, _WowsCommerce_purchaseTwoFactor, _WowsCommerce_transactionId, _WowsCommerce_createDevPanel, _WowsCommerce_createAnalytics;
const PlatformProviders = {
    [PLATFORMS.WEB]: WebPlatform,
    [PLATFORMS.EGS]: EpicGamesStorePlatform,
    [PLATFORMS.STEAM]: SteamPlatform,
    [PLATFORMS.WIN_STORE]: WinStorePlatform,
};
class WowsCommerce {
    constructor(projectSettings) {
        _WowsCommerce_instances.add(this);
        _WowsCommerce_platform.set(this, void 0);
        _WowsCommerce_platformProvider.set(this, void 0);
        _WowsCommerce_checkAccountCompletionIntervalId.set(this, void 0);
        _WowsCommerce_checkAccountCompletionIterationCount.set(this, void 0);
        _WowsCommerce_currentProduct.set(this, void 0);
        _WowsCommerce_activePaymentMethod.set(this, void 0);
        _WowsCommerce_tsv.set(this, void 0);
        _WowsCommerce_purchaseTwoFactor.set(this, void 0);
        _WowsCommerce_transactionId.set(this, void 0);
        settings.setSettings(projectSettings);
        if (settings.showDevPanel) {
            __classPrivateFieldGet(this, _WowsCommerce_instances, "m", _WowsCommerce_createDevPanel).call(this, settings);
        }
        __classPrivateFieldSet(this, _WowsCommerce_platform, detectPlatform(), "f");
        __classPrivateFieldSet(this, _WowsCommerce_platformProvider, new PlatformProviders[__classPrivateFieldGet(this, _WowsCommerce_platform, "f")](), "f");
        log('Settings', settings);
        log('Platform', __classPrivateFieldGet(this, _WowsCommerce_platform, "f"));
        if (settings.dwhExportEnabled && settings.dwhExportUrl) {
            __classPrivateFieldGet(this, _WowsCommerce_instances, "m", _WowsCommerce_createAnalytics).call(this, settings, __classPrivateFieldGet(this, _WowsCommerce_platform, "f"));
        }
        events.subscribe(EVENTS.CHECK_PURCHASE_RESTRICTION, () => {
            this.checkPurchaseRestrictionByCountryMismatch();
        });
        events.subscribe(EVENTS.CHECK_USER_COUNTRY_IS_SET, () => {
            this.checkPurchaseRestrictionByUserCountryIsSet();
        });
        __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIntervalId, null, "f");
        __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIterationCount, 0, "f");
        currentProduct.subscribe((value) => (__classPrivateFieldSet(this, _WowsCommerce_currentProduct, value, "f")));
        activePaymentMethod.subscribe((value) => (__classPrivateFieldSet(this, _WowsCommerce_activePaymentMethod, value, "f")));
        tsv.subscribe((value) => (__classPrivateFieldSet(this, _WowsCommerce_tsv, value, "f")));
        purchaseTwoFactor.subscribe((value) => (__classPrivateFieldSet(this, _WowsCommerce_purchaseTwoFactor, value, "f")));
        transactionId.subscribe((value) => (__classPrivateFieldSet(this, _WowsCommerce_transactionId, value, "f")));
    }
    checkPurchaseRestrictionByCountryMismatch() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.user();
            if (!user.canPurchase) {
                const isClosable = settings.fraudDetectionClosableByPlatform ? __classPrivateFieldGet(this, _WowsCommerce_platform, "f") !== PLATFORMS.WIN_STORE : true;
                const alreadyShownKey = `restriction_popup_shown_${user.userId}`;
                if (__classPrivateFieldGet(this, _WowsCommerce_platform, "f") !== PLATFORMS.WIN_STORE) {
                    if (getStorageItem(alreadyShownKey)) {
                        return;
                    }
                    setStorageItem(alreadyShownKey, true);
                }
                addEvent(DWH_EVENTS.FRAUD_PROTECTION_SHOW_COUNTRY_POPUP, {
                    closable: isClosable || null,
                });
                const { default: createPurchaseRestrictionModal } = yield import('../ui/PurchaseRestrictions/PurchaseRestrictionByCountryMismatch/index.js');
                createPurchaseRestrictionModal(settings.language, user.platformCountry, user.spaCountry, settings.userSupportUrl, isClosable, events);
            }
        });
    }
    products(customStorefrontName) {
        return __awaiter(this, void 0, void 0, function* () {
            return __classPrivateFieldGet(this, _WowsCommerce_platformProvider, "f").products(customStorefrontName);
        });
    }
    categories(customStorefrontName) {
        return __awaiter(this, void 0, void 0, function* () {
            return __classPrivateFieldGet(this, _WowsCommerce_platformProvider, "f").categories(customStorefrontName);
        });
    }
    getProductVariablePrice(productCode, quantity, customStorefrontName) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield __classPrivateFieldGet(this, _WowsCommerce_platformProvider, "f").getProductVariablePrice(productCode, quantity, customStorefrontName);
        });
    }
    checkout(externalProductId, quantity = 1, customStorefrontName, customFields, meta = {}) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.user();
            const products = yield this.products(customStorefrontName);
            const product = products.find((product) => product.externalProductId === externalProductId);
            if (product) {
                if (product.externalShopPurchaseUrl) {
                    openUrl(product.externalShopPurchaseUrl);
                    return null;
                }
                if (!((_a = user.regional) === null || _a === void 0 ? void 0 : _a.purchaseIsAllowed) && ((_b = user.regional) === null || _b === void 0 ? void 0 : _b.redirectUrl)) {
                    openUrl(user.regional.redirectUrl);
                    return null;
                }
                addEvent(DWH_EVENTS.PURCHASE_REQUEST, {
                    bundle_id: product.externalProductId,
                    currency: product.currency,
                    price: product.price,
                });
            }
            const checkoutResult = yield __classPrivateFieldGet(this, _WowsCommerce_platformProvider, "f").checkout(externalProductId, quantity, customStorefrontName, customFields);
            const dwhResult = {
                [PURCHASE_STATUSES.SUCCESS]: DWH_EVENTS.PURCHASE_SUCCESS,
                [PURCHASE_STATUSES.ERROR]: DWH_EVENTS.PURCHASE_ERROR,
                [PURCHASE_STATUSES.CANCELLED]: DWH_EVENTS.PURCHASE_CANCELED,
            }[checkoutResult.purchaseStatus];
            addEvent(dwhResult, Object.assign({ bundle_id: externalProductId }, meta));
            return checkoutResult;
        });
    }
    user(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return __classPrivateFieldGet(this, _WowsCommerce_platformProvider, "f").user(force);
        });
    }
    get analytics() {
        return {
            addEvent(eventName, payload) {
                return addEvent(eventName, payload);
            },
        };
    }
    subscribe(eventName, callback) {
        events.subscribe(eventName, callback);
    }
    needShowAccountCompletionMessage() {
        return __awaiter(this, void 0, void 0, function* () {
            if (settings.accountCompletionEnableForce) {
                return true;
            }
            if (!settings.accountCompletionEnable) {
                return false;
            }
            if (!settings.accountCompletionPlatforms.includes(__classPrivateFieldGet(this, _WowsCommerce_platform, "f"))) {
                return false;
            }
            const user = yield this.user();
            if (!settings.accountCompletionCountries.includes(user.platformCountry.toUpperCase())) {
                return false;
            }
            return user.isAccountUncompleted === true;
        });
    }
    isAccountCompleted() {
        return __classPrivateFieldGet(this, _WowsCommerce_platformProvider, "f").isAccountCompleted();
    }
    startCheckAccountCompletion(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this.isAccountCompleted()) {
                log('check account completion:', 'account completed');
                callback();
                return;
            }
            if (__classPrivateFieldGet(this, _WowsCommerce_checkAccountCompletionIntervalId, "f") !== null) {
                console.info('check account completion:', 'timer already running, reset iteration counter');
                __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIterationCount, 0, "f");
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIntervalId, setInterval(() => __awaiter(this, void 0, void 0, function* () {
                if (yield this.isAccountCompleted()) {
                    log('check account completion:', 'account completed');
                    clearInterval(__classPrivateFieldGet(this, _WowsCommerce_checkAccountCompletionIntervalId, "f"));
                    __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIntervalId, null, "f");
                    __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIterationCount, 0, "f");
                    callback();
                }
                else {
                    __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIterationCount, +__classPrivateFieldGet(this, _WowsCommerce_checkAccountCompletionIterationCount, "f") + 1, "f");
                    log('check account completion: timeout, retry request, iteration', __classPrivateFieldGet(this, _WowsCommerce_checkAccountCompletionIterationCount, "f"));
                    if (__classPrivateFieldGet(this, _WowsCommerce_checkAccountCompletionIterationCount, "f") > settings.checkAccountCompletionRetryCount) {
                        clearInterval(__classPrivateFieldGet(this, _WowsCommerce_checkAccountCompletionIntervalId, "f"));
                        __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIntervalId, null, "f");
                        __classPrivateFieldSet(this, _WowsCommerce_checkAccountCompletionIterationCount, 0, "f");
                    }
                }
            }), settings.checkAccountCompletionTimeout), "f");
        });
    }
    isNeedShowMorePaymentMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            if (settings.showMorePaymentMethodsCountries.indexOf('*') > -1) {
                return Promise.resolve(true);
            }
            const spaCountry = (yield this.user()).spaCountry.toUpperCase();
            return Promise.resolve(settings.showMorePaymentMethodsCountries.indexOf(spaCountry) > -1);
        });
    }
    checkPurchaseRestrictionByUserCountryIsSet(isClosable = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (__classPrivateFieldGet(this, _WowsCommerce_platform, "f") !== PLATFORMS.WEB) {
                return;
            }
            const user = yield this.user(true);
            if (!user) {
                return;
            }
            if (!user.userId || user.spaCountry) {
                return;
            }
            addEvent(DWH_EVENTS.USER_COUNTRY_NOT_SET_POPUP, {});
            const { default: createPurchaseRestrictionModal } = yield import('../ui/PurchaseRestrictions/PurchaseRestrictionByUserCountryNotSet/index.js');
            createPurchaseRestrictionModal(settings.language, settings.userSetCountryUrl, isClosable, events);
        });
    }
    handlePurchaseProduct(isClosable = true, storefrontName) {
        return __awaiter(this, void 0, void 0, function* () {
            const { default: appPurchaseProduct } = yield import('../ui/PurchaseProduct/index.js');
            appPurchaseProduct(isClosable, events);
            const currentProduct = __classPrivateFieldGet(this, _WowsCommerce_currentProduct, "f").externalProductId;
            addEvent(DWH_EVENTS.OPEN_PURCHASE_POPUP_FROM_BUNDLE, { bundle_id: currentProduct });
            yield purchaseProduct(__classPrivateFieldGet(this, _WowsCommerce_currentProduct, "f"), __classPrivateFieldGet(this, _WowsCommerce_activePaymentMethod, "f"), __classPrivateFieldGet(this, _WowsCommerce_tsv, "f"), __classPrivateFieldGet(this, _WowsCommerce_purchaseTwoFactor, "f"), __classPrivateFieldGet(this, _WowsCommerce_transactionId, "f"), true, storefrontName);
        });
    }
    handleChangeCurrentProduct(product, storefrontName) {
        changeCurrentProduct(product, storefrontName);
    }
    handleChangeProductPriceByQuantity(product, variablePriceData, quantity, storefrontName) {
        product.updateQuantity(quantity);
        product.updateVariableProductPrice(variablePriceData);
        updateProduct(product, storefrontName);
        return product;
    }
}
_WowsCommerce_platform = new WeakMap(), _WowsCommerce_platformProvider = new WeakMap(), _WowsCommerce_checkAccountCompletionIntervalId = new WeakMap(), _WowsCommerce_checkAccountCompletionIterationCount = new WeakMap(), _WowsCommerce_currentProduct = new WeakMap(), _WowsCommerce_activePaymentMethod = new WeakMap(), _WowsCommerce_tsv = new WeakMap(), _WowsCommerce_purchaseTwoFactor = new WeakMap(), _WowsCommerce_transactionId = new WeakMap(), _WowsCommerce_instances = new WeakSet(), _WowsCommerce_createDevPanel = function _WowsCommerce_createDevPanel(settings) {
    return __awaiter(this, void 0, void 0, function* () {
        const { default: createDevPanel } = yield import('../ui/DevPanel/index.js');
        createDevPanel(settings);
    });
}, _WowsCommerce_createAnalytics = function _WowsCommerce_createAnalytics(settings, platform) {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield this.user();
        createAnalytics(settings, platform, user.userId);
        addEvent(DWH_EVENTS.APP_START);
    });
};

export { WowsCommerce as default };
//# sourceMappingURL=index.js.map
